import { useState, useEffect, useRef, useCallback } from "react";
import LanguageDropdown from "../../components/LanguageDropdown/LanguageDropdown";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ClockIcon,
  ComputerDesktopIcon,
  ArrowsPointingOutIcon,
  ArrowTopRightOnSquareIcon,
  NoSymbolIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { AcademicCapIcon } from "@heroicons/react/24/solid";
import {
  exchangeLaunchCode,
  startSession,
} from "../../services/External/ExternalSessionService";
import SimpleLoader from "../../components/Loader/SimpleLoader";
import { isCanceledError, resolveExamErrorKey } from "../../utils/examError";
import { eyeBreakCount } from "../../utils/eyeBreaks";

const formatMinutes = (n) => (Number.isInteger(n) ? n : n.toFixed(1));

export default function ExternalLaunch() {
  const { sessionUuid } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  // A language switch must not re-run the exchange (it rotates the session
  // token), so the effect reads t through a ref.
  const tRef = useRef(t);
  tRef.current = t;

  const [step, setStep] = useState("exchanging");
  const [timing, setTiming] = useState(null);
  const [error, setError] = useState(null);
  const [errorHeading, setErrorHeading] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isActiveOnAnotherDevice, setIsActiveOnAnotherDevice] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const codeRef = useRef(searchParams.get("code"));
  const activeSessionUuidRef = useRef(null);

  const handleRetry = useCallback(() => {
    setError(null);
    setErrorHeading(null);
    setIsActiveOnAnotherDevice(false);
    setAcknowledged(false);
    setRetryCount((c) => c + 1);
  }, []);

  // Every launch starts in Kazakh, whatever the previous user of this
  // computer picked; the student can switch on the rules page.
  useEffect(() => {
    i18n.changeLanguage("kk");
  }, [i18n]);

  useEffect(() => {
    const t = tRef.current;
    const code = codeRef.current;
    if (!code) {
      setErrorHeading(t("exam_taking.failed_to_start_exam"));
      setError(t("exam_taking.invalid_launch_code"));
      setStep("error");
      return;
    }

    const controller = new AbortController();
    let isCurrent = true;

    const launch = async () => {
      try {
        setStep("exchanging");
        const exchangeData = await exchangeLaunchCode({
          sessionUuid,
          data: { launch_code: code },
          signal: controller.signal,
        });
        if (!isCurrent) return;

        sessionStorage.setItem("sessionToken", exchangeData.session_token);
        if (exchangeData.callback_url) {
          sessionStorage.setItem("portalUrl", exchangeData.callback_url);
        }

        const activeSessionUuid = exchangeData.session_uuid || sessionUuid;
        activeSessionUuidRef.current = activeSessionUuid;

        setTiming({
          duration: exchangeData.duration_minutes || 0,
          interval: exchangeData.eye_break_interval_minutes || 0,
          seconds: exchangeData.eye_break_seconds || 0,
        });
        setStep("rules");
      } catch (err) {
        if (!isCurrent || isCanceledError(err)) return;

        const errorCode = err.response?.data?.error_code;
        const rawMessage = err.response?.data?.error || err.response?.data?.detail;

        const isDeviceError =
          errorCode === "ACTIVE_ON_ANOTHER_DEVICE" ||
          (rawMessage && rawMessage.includes("active on another device"));

        if (isDeviceError) {
          setIsActiveOnAnotherDevice(true);
          setErrorHeading(t("exam_taking.failed_to_start_exam"));
          setError(t("exam_taking.active_on_another_device"));
          setStep("error");
          return;
        }

        const isSessionError =
          errorCode === "SESSION_EXPIRED" || errorCode === "SESSION_UNAVAILABLE";
        setErrorHeading(
          isSessionError
            ? t("exam_taking.session_expired")
            : t("exam_taking.failed_to_start_exam")
        );

        const key = resolveExamErrorKey(err, null);
        setError(key ? t(key) : rawMessage || t("exam_taking.invalid_launch_code"));
        setStep("error");
      }
    };

    launch();
    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, [sessionUuid, navigate, retryCount]);

  const handleStart = async () => {
    const uuid = activeSessionUuidRef.current;
    setStep("starting");
    try {
      await startSession({ sessionUuid: uuid });
      navigate(`/launch/${uuid}/exam`, { replace: true });
    } catch (err) {
      if (isCanceledError(err)) return;

      const errorCode = err.response?.data?.error_code;
      const rawMessage = err.response?.data?.error;

      const isSessionError =
        errorCode === "SESSION_EXPIRED" || errorCode === "SESSION_UNAVAILABLE";
      setErrorHeading(
        isSessionError
          ? t("exam_taking.session_expired")
          : t("exam_taking.failed_to_start_exam")
      );

      const key = resolveExamErrorKey(err, null);
      setError(key ? t(key) : rawMessage || t("exam_taking.failed_to_start_exam"));
      setStep("error");
    }
  };

  // Students ask how long the exam really is: spell out work time + breaks.
  const breakCount = timing
    ? eyeBreakCount(timing.duration * 60, timing.interval * 60, timing.seconds)
    : 0;
  const breakMinutes = breakCount ? (breakCount * timing.seconds) / 60 : 0;
  const rules = [
    { icon: ArrowsPointingOutIcon, key: "rule_fullscreen" },
    { icon: ArrowTopRightOnSquareIcon, key: "rule_tab" },
    { icon: ComputerDesktopIcon, key: "rule_split_screen" },
    { icon: NoSymbolIcon, key: "rule_copy" },
    { icon: ClockIcon, key: "rule_timer" },
    ...(breakCount > 0
      ? [{
          icon: EyeIcon,
          key: "rule_eye_breaks",
          values: {
            total: timing.duration,
            work: formatMinutes(timing.duration - breakMinutes),
            breaks: formatMinutes(breakMinutes),
            minutes: timing.interval,
            seconds: timing.seconds,
          },
        }]
      : []),
    { icon: ExclamationTriangleIcon, key: "rule_leave" },
  ];

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-8">
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm motion-safe:animate-fade-in sm:p-8">
        <div className="mb-6 flex items-center justify-between gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-sm shadow-blue-600/25">
            <AcademicCapIcon className="h-5 w-5" aria-hidden="true" />
          </span>
          <LanguageDropdown />
        </div>
        {step === "error" ? (
          <>
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-3xl font-bold text-red-600 ring-1 ring-inset ring-red-100">!</div>
            <h2 className="mb-2 text-lg font-semibold tracking-tight text-slate-900">
              {errorHeading}
            </h2>
            <p className="mb-6 text-sm leading-6 text-slate-500">{error}</p>
            {!isActiveOnAnotherDevice && (
              <button
                onClick={handleRetry}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 text-sm font-medium text-white shadow-sm shadow-blue-600/25 transition duration-200 hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
              >
                <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                {t("exam_taking.retry")}
              </button>
            )}
          </>
        ) : step === "rules" ? (
          <>
            <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-left sm:p-5">
              <h2 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-amber-800">
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                {t("exam_taking.exam_rules")}
              </h2>
              <ul className="flex flex-col gap-2.5">
                {rules.map(({ icon: Icon, key, values }) => (
                  <li key={key} className="flex items-start gap-2.5 text-sm leading-6 text-amber-900">
                    <Icon className="mt-1 h-4 w-4 flex-shrink-0 text-amber-600" />
                    <span>{t(`exam_taking.${key}`, values)}</span>
                  </li>
                ))}
              </ul>
              <label className="mt-4 flex cursor-pointer select-none items-center gap-3 rounded-lg border border-amber-200 bg-white px-3 py-2.5 transition duration-200 hover:border-amber-300">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 transition focus:ring-4 focus:ring-blue-600/15 focus:ring-offset-0"
                />
                <span className="text-sm font-medium text-slate-900">
                  {t("exam_taking.rules_acknowledge")}
                </span>
              </label>
            </div>
            <button
              onClick={handleStart}
              disabled={!acknowledged}
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 text-base font-semibold text-white shadow-lg shadow-blue-600/25 transition duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 enabled:hover:bg-blue-700 enabled:active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
            >
              {t("exam_taking.start_exam")}
            </button>
          </>
        ) : (
          <>
            <SimpleLoader className="h-8 w-8" />
            <p className="mt-4 text-sm text-slate-500">
              {step === "exchanging"
                ? t("exam_taking.loading_exam")
                : t("exam_taking.starting_exam")}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
